import React, { useMemo, useRef } from 'react';
import { SteamOptionsType, TableHeight } from '../../constants';
import { useCompareKeyMetricsChart } from '../../hooks';
import {
  dealWithMentionsSentimentChart,
  getGameColorOrName,
} from '../../utils';
import { echartsContent, sortMapAuxiliaryFunc } from '@/pages/opinion/utils';
import { formatTimeXAxisText } from '@op/utils/chart';
import useLanguage from '@op/hooks/useLanguage';
import { formatChartParamsDate } from '@op/components/ChartCommon/formatter';
import EChartCommon, { getAxisPointer } from '@op/components/EChartCommon';
import TableCommonHook from '@op/components/TableCommonHook';
import './index.scss';
import { reviewsFormat } from '@op/utils/value';
import { formatDateFunc } from '@op/utils/time';
import TableColumn from '../../component/TableColumn';
import CardCommon from '@op/components/CardCommon';
import { getGameDataIndex } from '@op/components/TableCommonHook/hooks/useCompareKeyMetricsColumns';
import ProgressColumn from '../../component/ProgressColumn';
// import moment from 'moment';

export default function SteamCharts({
  filter,
  tab,
  chartType,
  gameData,
  subType,
  options: steamOptions,
}: {
  filter: any;
  tab: SteamOptionsType;
  chartType: string;
  gameData: {
    [id: string]: {
      name: string;
      color: string;
    };
  }[];
  subType: string;
  options: {
    text: string;
    value: string;
    format?: (value: number) => number | string;
  }[];
}) {
  const { formatMessage } = useLanguage();

  const getGameData = getGameColorOrName(gameData);

  const { tooltip, formatData, dataType } = useMemo(() => {
    const select =
      steamOptions?.filter((item) =>
        subType ? item.value === subType : item.value === tab,
      )[0] || {};
    const { text, format, dataType } = select || {};
    return {
      tooltip: text ? formatMessage({ id: text }) : '',
      formatData: format ? format : (value: number) => reviewsFormat(value),
      dataType: dataType ?? '',
    };
  }, [formatMessage, steamOptions, subType, tab]);

  const params = useMemo(
    () => ({
      ...filter,
      stats_types: ['game_store'],
      tab_type: 'steam',
      by_type: tab,
      ...(subType ? { sub_type: `${tab}_${subType}` } : {}),
    }),
    [filter, subType, tab],
  );

  const { data: statistics, loading } = useCompareKeyMetricsChart({
    filter: params,
    dealWithToChart: dealWithMentionsSentimentChart,
  });

  const xAxisIntervalLimit = 8;
  const chartRef = useRef(null);
  const { xAxisData, yAxisData, tableData } = statistics;
  const hasStatistics = Array.isArray(xAxisData) && xAxisData.length > 0;

  // const { isEmpty, emptyDescText, hasEmptyTitle, hasEmptyImg } = useMemo(() => {
  //   const formatDate = (date: moment.MomentInput) => moment(date, 'YYYY-MM-DD');

  //   let isEmpty = !hasStatistics;
  //   let emptyDescText = undefined;
  //   let hasEmptyTitle = true;
  //   let hasEmptyImg = true;
  //   if (isEmpty && hasEmptyTitle) {
  //     console.log(
  //       '=====',
  //       xAxisData,
  //       yAxisData,
  //       'tabl' + tab,
  //       'subType :: ' + subType,
  //     );
  //     const key = subType ? `${tab}_${subType}` : tab;
  //     if (
  //       [
  //         'recent_reviews',
  //         'recent_reviews_positive',
  //         'recent_reviews_negative',
  //       ].includes(key)
  //     ) {
  //       const valid =
  //         yAxisData?.filter((val) => {
  //           const item = getGameData(val?.name, 'all');
  //           const { release_time: releaseTime } = item;

  //           return (
  //             releaseTime &&
  //             formatDate(filter?.end_time).diff(
  //               formatDate(releaseTime),
  //               'days',
  //             ) > 30
  //           );
  //         }) || [];
  //       if (valid.length === 0) {
  //         emptyDescText =
  //           '“Recent Reviews” refer to reviews from the past 30 days. Therefore, data is only available starting from the 31st day after launch.';
  //         hasEmptyTitle = false;
  //         hasEmptyImg = false;
  //       }
  //     }

  //     if (
  //       [
  //         'all_reviews',
  //         'all_reviews_positive',
  //         'all_reviews_negative',
  //       ].includes(key)
  //     ) {
  //       const valid =
  //         yAxisData?.filter((val) => {
  //           const item = getGameData(val?.name, 'all');
  //           const { release_time: releaseTime } = item;

  //           return (
  //             releaseTime &&
  //             formatDate(filter?.end_time).isAfter(
  //               formatDate(releaseTime),
  //               'days',
  //             )
  //           );
  //         }) || [];
  //       if (valid.length === 0) {
  //         emptyDescText = 'Data available after launch';
  //         hasEmptyTitle = false;
  //         hasEmptyImg = false;
  //       }
  //     }
  //   }

  //   return {
  //     isEmpty,
  //     emptyDescText,
  //     hasEmptyTitle,
  //     hasEmptyImg,
  //   };
  // }, [filter, getGameData, hasStatistics, subType, tab, xAxisData, yAxisData]);

  const columns = useMemo(
    () => [
      {
        header: formatMessage({ id: 'Date' }),
        key: 'time',
        align: 'left',
        render: (cvm: any) => formatDateFunc(params?.date_type)(cvm.time),
      },
      {
        header: formatMessage({ id: 'Game' }),
        key: 'name',
        align: 'left',
        render: (cvm: any) => {
          const item: any = getGameData(cvm?.name, 'all');
          return <TableColumn name={item?.entity_name} item={item} />;
        },
      },
      {
        header: tooltip,
        key: 'value',
        align: 'center',
        render: (cvm: any) => (
          <ProgressColumn
            percentage={cvm.ratio}
            value={cvm.value}
            oldTime={[]}
            newTime={[]}
            oldValue={''}
            formateValue={formatData}
          ></ProgressColumn>
        ),
      },
    ],
    [formatMessage, params?.date_type, tooltip],
  );

  const options = useMemo(() => {
    if (hasStatistics) {
      let legendData: {
        name: string;
        itemStyle: {
          color: string;
        };
      }[] = [];
      let seriesData: {
        itemStyle?: any;
        barMaxWidth?: string;
        barGap?: string;
        label?: any;
        name: string;
        type: string;
        stack?: string;
        data: (number | null)[];
      }[] = [];
      yAxisData.map((item: { name: string; data: any[] }) => {
        const color = getGameData(item.name, 'color');
        seriesData.push({
          name: item.name,
          type: chartType,
          stack: chartType === 'bar' ? 'total' : '',
          data: item.data.map((value) => {
            if (['recent_reviews', 'all_reviews'].includes(tab) && value === 0)
              return null;
            return value;
          }),
          barMaxWidth: '70px',
          itemStyle: { color },
        });

        legendData.push({
          name: item.name,
          itemStyle: { color },
        });
      });
      const sortMap = getGameDataIndex(gameData);
      legendData.sort(sortMapAuxiliaryFunc(sortMap, (item) => item.name));
      const option = {
        tooltip: {
          trigger: 'axis',
          borderWidth: 0,
          padding: 0,
          confine: true,
          extraCssText: 'z-index:8',
          formatter: (data: any[]) => {
            const sumKey = data[0].axisValue;
            let timeText = '';
            if (params?.date_type) {
              timeText = formatDateFunc(params?.date_type)(sumKey);
            }
            return formatChartParamsDate({
              params: data,
              title: timeText,
              rightTitle: tooltip,
              listTitleFunc: (item) => getGameData(item.seriesName),
              valueFormat: formatData,
              isSort: true,
            });
          },
        },
        legend: {
          data: legendData,
          formatter: (params: string) => getGameData(params),
        },
        axisPointer: getAxisPointer((data) =>
          formatTimeXAxisText(data.value, params?.date_type),
        ),
        xAxis: {
          type: 'category',
          boundaryGap: ['20%', 0],
          data: xAxisData,
          axisLabel: {
            interval: xAxisData?.length > xAxisIntervalLimit ? 'auto' : 0,
            fontSize: 10,
            showMaxLabel: true,
            formatter: (value: any) =>
              formatTimeXAxisText(value, params?.date_type),
          },
        },
        yAxis: {
          type: 'value',
          axisLabel: {
            formatter: (value: any) => echartsContent(value),
            fontSize: 11,
          },
        },
        series: seriesData,
      };

      if (dataType === 'percent') {
        option.yAxis = {
          type: 'value',
          splitNumber: 5,
          max: 1,
          min: 0,
          axisLabel: {
            formatter: (value: any) => `${(value * 100).toFixed(0)}%`,
            fontSize: 11,
          },
        };
      }

      return option;
    }
    return {};
  }, [
    hasStatistics,
    yAxisData,
    gameData,
    xAxisData,
    dataType,
    getGameData,
    chartType,
    tab,
    params?.date_type,
    tooltip,
    formatData,
  ]);

  return (
    <div ref={chartRef} className="dbo-steam-chart">
      <CardCommon
        contentCard
        loading={loading}
        loadingHeight={TableHeight.CHART_LOADING}
        empty={!hasStatistics}
        emptyHeight={TableHeight.CHART_LOADING}
        // emptyDescText={emptyDescText}
        // hasEmptyTitle={hasEmptyTitle}
        // hasEmptyImg={hasEmptyImg}
      >
        {chartType !== 'table' ? (
          <EChartCommon
            options={options}
            leftTitle={tooltip}
            height={TableHeight.CHART}
          />
        ) : (
          <TableCommonHook
            recordKey={'comment_uin'}
            records={tableData || []}
            total={tableData?.length || 0}
            columns={columns}
            sortColumns={['time', 'value']}
            hasPageable={false}
            minWidth="auto"
            sortType={'local'}
            bordered="all"
            maxHeight={TableHeight.CHART_TABLE}
          />
        )}
      </CardCommon>
    </div>
  );
}
